// Contacts name normalization — the CANONICAL, framework-agnostic spec for the
// legacy-name-fix + multi-contact-person feature. This module is the SINGLE
// SOURCE OF TRUTH for the validation + classification rules; its vitest suite
// (nameNormalize.test.ts) is the spec's executable guard.
//
// The Cloudflare Worker (api/src/index.ts) carries a DELIBERATE, hand-mirrored
// duplicate of these helpers (parsePersonName / classifyLegacyName /
// splitCoupleName / composeHouseholdDisplay / normalizeIncomingContactName +
// validateContactDisplayName) because the worker build cannot import from
// src/lib (separate bundle) — the same precedent as normalizeUsContactPhone vs
// src/lib/phone/format.ts. The two implementations share ONE spec; when a rule
// changes here, change it there too and keep this test suite green.
//
// Pure: no DOM, no React, no I/O — safe under the vitest `node` env that gates
// `npm test` (see vitest.config.ts, include src/**/*.test.ts).
//
// Rule spec (see the feature brief "VALIDATION RULES"):
//   PERSON inputs — commas rejected EXCEPT one comma before a generational/
//   professional suffix (Jr/Sr/II/III/IV/V/Esq/MD/DDS/DO/PhD/CPA); '&' and a
//   capitalized ' and ' couple rejected (offer a split); '+' ';' '/' rejected;
//   email/phone/street-address shapes rejected. Hyphens, apostrophes (' and ’),
//   initials, Unicode letters, single-word names, and space-separated suffixes
//   are ALWAYS allowed. A last-first WITHOUT a comma ("Bettencourt Jeffrey") is
//   NOT blockable (no reliable heuristic) — the review queue owns that class.
//   ORG inputs — '&', ' and ', and commas (corporate suffixes) are all allowed;
//   only email/phone/address shapes are rejected.

// Minification-proof shipped-bundle marker. The ship LIVE-verify greps the
// served JS for this literal to prove src/lib/contacts actually shipped. It is
// stamped onto every validation result (below) so it can never be tree-shaken
// away while validatePersonNameInput is in the bundle.
export const NAME_NORMALIZE_MARKER = 'cp-name-normalize-v1'
export const NAME_NORMALIZE_SPEC_VERSION = NAME_NORMALIZE_MARKER

// ---------------------------------------------------------------------------
// Token vocabularies
// ---------------------------------------------------------------------------

/** Generational / professional suffixes permitted on a person name (case- and
 *  period-insensitive). A single comma is allowed ONLY when it is immediately
 *  followed by one of these. */
export const SUFFIX_TOKENS = [
  'Jr', 'Sr', 'II', 'III', 'IV', 'V', 'Esq', 'MD', 'DDS', 'DO', 'PhD', 'CPA',
] as const

/** Corporate suffixes. A person name whose comma is followed by one of these
 *  is an organization mis-typed as a person → guidance to switch the type. */
export const ORG_SUFFIX_TOKENS = [
  'Inc', 'LLC', 'L.L.C.', 'Corp', 'Co', 'Ltd', 'PA', 'PLLC', 'PC',
] as const

/** Organization words. Any token equal (case-insensitively, punctuation
 *  stripped) to one of these DEMOTES a comma/ampersand person row out of the
 *  auto-fix tiers into the review queue (client_type is dirty — orgs live in
 *  the 'person'/'individual' buckets). Generous by design: a false demotion
 *  only sends a fixable row to human review (safe); a MISSED org word would let
 *  an auto-swap corrupt a real company name (unsafe). */
export const ORG_WORDS = [
  'Inc', 'LLC', 'LLP', 'LP', 'Corp', 'Corporation', 'Co', 'Company', 'Companies',
  'Ltd', 'Limited', 'Assoc', 'Assn', 'Associates', 'Association', 'Development',
  'Center', 'Centre', 'Realty', 'Realtors', 'Golf', 'Club', 'Shopping',
  'Properties', 'Property', 'Condo', 'Condominium', 'HOA', 'Group', 'Partners',
  'Partnership', 'Enterprise', 'Enterprises', 'Holdings', 'Services', 'Service',
  'Management', 'Bank', 'Trust', 'Foundation', 'Ministries', 'Church', 'School',
  'Sons', 'Bros', 'Brothers', 'Construction', 'Painting', 'Builders', 'Roofing',
  'Plumbing', 'Electric', 'Homes', 'Estates', 'Plaza', 'Industries', 'Systems',
] as const

const SUFFIX_SET = new Set(SUFFIX_TOKENS.map((t) => t.toUpperCase()))
const ORG_SUFFIX_SET = new Set(ORG_SUFFIX_TOKENS.map((t) => t.replace(/\./g, '').toUpperCase()))
const ORG_WORD_SET = new Set(ORG_WORDS.map((t) => t.replace(/[.,]/g, '').toUpperCase()))

const NAME_CHAR = "\\p{L}'’.\\-" // letters (any script), apostrophes, period, hyphen

function isSuffixToken(tok: string): boolean {
  return SUFFIX_SET.has(tok.replace(/\./g, '').toUpperCase())
}
function isCorpSuffixToken(tok: string): boolean {
  return ORG_SUFFIX_SET.has(tok.replace(/\./g, '').toUpperCase())
}

// ---------------------------------------------------------------------------
// parsePersonName — structural parse of a single person name.
// Handles "First [Middle] Last [Suffix]", a leading "Last, First …" comma form
// (reordered), a trailing "First Last, Suffix" comma form, and single-word
// names. Used by the {{firstName}}/{{lastName}} merge-field renderer (fallback
// when a client has no primary contact_person) and by the swap suggestion.
// ---------------------------------------------------------------------------

export interface ParsedName {
  firstName: string
  middle: string
  lastName: string
  suffix: string
  /** Canonical "First [Middle] Last[ Suffix]" display. */
  display: string
}

export function parsePersonName(raw: string): ParsedName {
  const name = (raw ?? '').trim()
  const empty: ParsedName = { firstName: '', middle: '', lastName: '', suffix: '', display: '' }
  if (!name) return empty

  let working = name
  let suffix = ''

  const ci = working.indexOf(',')
  if (ci >= 0) {
    const before = working.slice(0, ci).trim()
    const after = working.slice(ci + 1).trim()
    const beforeTokens = before.split(/\s+/).filter(Boolean)
    const afterTokens = after.split(/[\s,]+/).filter(Boolean)
    const afterEndsWithSuffix = afterTokens.length > 0 && isSuffixToken(afterTokens[afterTokens.length - 1])

    if (afterEndsWithSuffix && beforeTokens.length >= 2 && afterTokens.length === 1) {
      // "First Last, Suffix" — before is the full name; comma only fenced a suffix.
      suffix = afterTokens[afterTokens.length - 1]
      working = before
    } else {
      // "Last, First [Middle] [Suffix]" — reorder to First … Last [Suffix].
      if (afterEndsWithSuffix) suffix = afterTokens.pop() as string
      const first = afterTokens.join(' ')
      working = (first ? first + ' ' : '') + before
    }
  } else {
    const toks = working.split(/\s+/).filter(Boolean)
    if (toks.length >= 2 && isSuffixToken(toks[toks.length - 1])) {
      suffix = toks.pop() as string
      working = toks.join(' ')
    }
  }

  const tokens = working.split(/\s+/).filter(Boolean)
  let firstName = ''
  let middle = ''
  let lastName = ''
  if (tokens.length === 1) {
    firstName = tokens[0]
  } else if (tokens.length >= 2) {
    firstName = tokens[0]
    lastName = tokens[tokens.length - 1]
    middle = tokens.slice(1, -1).join(' ')
  }

  const display = [firstName, middle, lastName].filter(Boolean).join(' ') + (suffix ? ` ${suffix}` : '')
  return { firstName, middle, lastName, suffix, display: display.trim() }
}

// ---------------------------------------------------------------------------
// composeHouseholdDisplay — render a set of persons as ONE household display.
// Shared-surname collapse ("Alan Dorman" + "Karen Dorman" → "Alan & Karen
// Dorman"); otherwise join the full names ("Lynda Boros & Laurie Brown").
// Independent of parsePersonName to keep the module dependency graph acyclic.
// ---------------------------------------------------------------------------

export function composeHouseholdDisplay(persons: string[]): string {
  const clean = (persons || []).map((p) => (p ?? '').trim()).filter(Boolean)
  if (clean.length === 0) return ''
  if (clean.length === 1) return clean[0]

  const parts = clean.map((p) => p.split(/\s+/).filter(Boolean))
  const surnames = parts.map((t) => t[t.length - 1] ?? '')
  const allMultiToken = parts.every((t) => t.length >= 2)
  const sharedSurname =
    allMultiToken && surnames[0].length > 0 && surnames.every((s) => s.toLowerCase() === surnames[0].toLowerCase())

  if (sharedSurname) {
    const givens = parts.map((t) => t.slice(0, -1).join(' '))
    return `${givens.join(' & ')} ${surnames[0]}`
  }
  return clean.join(' & ')
}

// ---------------------------------------------------------------------------
// splitCoupleName — split a joined couple into two persons + a household
// display. Handles "Last, F1 & F2", "F1 & F2 Last", the different-surname
// "Last1, F1 & Last2, F2" form, and the capitalized ' and ' form. Returns null
// when the string is not a couple pattern.
// ---------------------------------------------------------------------------

export interface SplitCoupleResult {
  persons: string[]
  display: string
  /** true when both halves resolve to the SAME surname (Tier-B eligible). */
  sharedSurname: boolean
  form: 'amp' | 'and'
}

const COUPLE_AND_RE = new RegExp(`[A-Z][${NAME_CHAR}]*\\s+[aA][nN][dD]\\s+[A-Z][${NAME_CHAR}]*`, 'u')

/** True when the string joins two capitalized name tokens with a word-bounded
 *  ' and ' (case-insensitive). Word-boundary + capitalization keep "Anand",
 *  "Amanda", "Alexandra" unaffected. */
export function detectCoupleAnd(name: string): boolean {
  return COUPLE_AND_RE.test(name)
}

// LOOSE variant for the live INPUT VALIDATION path (cp-create-main-v1): a
// word-boundary standalone ' and ' between two name-like tokens, CASE-
// INSENSITIVE ("mark and lori edelman" fires like "Mark and Lori Edelman").
// The standalone-word requirement (whitespace on both sides) keeps names
// merely CONTAINING the letters — Anderson, Sandy, Andrea, Anand — unaffected.
// The legacy data-fix classifiers keep the stricter capitalized detectCoupleAnd
// so their measured tier behavior does not move.
const COUPLE_AND_LOOSE_RE = new RegExp(`[${NAME_CHAR}]+\\s+and\\s+[${NAME_CHAR}]+`, 'iu')

/** Case-insensitive couple-' and ' detection used by validatePersonNameInput. */
export function detectCoupleAndLoose(name: string): boolean {
  return COUPLE_AND_LOOSE_RE.test(name)
}

function swapSimpleLastFirst(s: string): string | null {
  const c = s.indexOf(',')
  if (c < 0) return null
  const last = s.slice(0, c).trim()
  const first = s.slice(c + 1).trim()
  if (!last || !first) return null
  return `${first} ${last}`
}
function surnameOf(full: string): string {
  const t = full.trim().split(/\s+/).filter(Boolean)
  return t.length ? t[t.length - 1] : ''
}

export function splitCoupleName(raw: string): SplitCoupleResult | null {
  const name = (raw ?? '').trim()
  if (!name) return null

  let form: 'amp' | 'and'
  let left: string
  let right: string

  if (name.includes('&')) {
    const idx = name.indexOf('&')
    if (name.indexOf('&', idx + 1) !== -1) return null // 2+ ampersands → too complex, review
    form = 'amp'
    left = name.slice(0, idx).trim()
    right = name.slice(idx + 1).trim()
  } else {
    // Case-insensitive name tokens (cp-create-main-v1) so the one-click split
    // works for "mark and lori edelman" too. Callers gate on detectCoupleAnd /
    // detectCoupleAndLoose, so the legacy classifiers' behavior is unchanged.
    const m = name.match(new RegExp(`^(.*?[\\p{L}][${NAME_CHAR}]*)\\s+[aA][nN][dD]\\s+([\\p{L}][${NAME_CHAR}]*.*)$`, 'u'))
    if (!m) return null
    form = 'and'
    left = m[1].trim()
    right = m[2].trim()
  }
  if (!left || !right) return null

  let persons: string[]
  let sharedSurname: boolean

  if (left.includes(',') && right.includes(',')) {
    // "Last1, F1 & Last2, F2" — each half is its own Last, First (surnames may differ).
    const p1 = swapSimpleLastFirst(left)
    const p2 = swapSimpleLastFirst(right)
    if (!p1 || !p2) return null
    persons = [p1, p2]
    sharedSurname = surnameOf(p1).length > 0 && surnameOf(p1).toLowerCase() === surnameOf(p2).toLowerCase()
  } else if (left.includes(',')) {
    // "Last, F1 & F2" — shared surname is the token before the comma.
    const c = left.indexOf(',')
    const last = left.slice(0, c).trim()
    const f1 = left.slice(c + 1).trim()
    const f2 = right.trim()
    if (!last || !f1 || !f2) return null
    persons = [`${f1} ${last}`, `${f2} ${last}`]
    sharedSurname = true
  } else {
    // "F1 & F2 Last" — shared surname is the last token of the right half.
    const rt = right.split(/\s+/).filter(Boolean)
    if (rt.length < 2) {
      persons = [left, right]
      sharedSurname = false
    } else {
      const last = rt[rt.length - 1]
      persons = [`${left} ${last}`, right]
      sharedSurname = true
    }
  }

  return { persons, display: composeHouseholdDisplay(persons), sharedSurname, form }
}

// ---------------------------------------------------------------------------
// classifyLegacyName — confidence-tier classifier for the data-fix job.
//   A  auto swap        "Last, First" → "First Last"        { proposed }
//   B  auto couple split                                    { persons, display }
//   C  review queue     org-like / note / different surname / multi-comma / junk
//   D  untouched        already clean OR out of scope (single-token, empty)
// Idempotent: classify("Jeffrey Bettencourt") === Tier D.
// The MANDATORY prior-art household-dupe guard + ingest/household reasons are
// applied SERVER-SIDE (they need D1) — this pure classifier never emits
// 'household_dupe' or 'ingest'.
// ---------------------------------------------------------------------------

export type LegacyNameTier = 'A' | 'B' | 'C' | 'D'

export type ClassifyResult =
  | { tier: 'A'; proposed: string }
  | { tier: 'B'; persons: string[]; display: string }
  | { tier: 'C'; reason: string; suggestion?: { proposed?: string; persons?: string[] } }
  | { tier: 'D' }

function hasOrgWord(name: string): boolean {
  const toks = name.split(/[\s,&]+/).filter(Boolean)
  for (const t of toks) {
    if (ORG_WORD_SET.has(t.replace(/[.,]/g, '').toUpperCase())) return true
  }
  return false
}
function hasNoteMarker(name: string): boolean {
  return /\sfor\s/i.test(name) || /\s-\s/.test(name)
}
function commaFollowedByCorpSuffix(name: string): boolean {
  const c = name.indexOf(',')
  if (c < 0) return false
  const first = name.slice(c + 1).trim().split(/[\s,]+/).filter(Boolean)[0]
  return !!first && isCorpSuffixToken(first)
}
function commaFollowedBySuffix(name: string): boolean {
  const c = name.indexOf(',')
  if (c < 0) return false
  const first = name.slice(c + 1).trim().split(/[\s,]+/).filter(Boolean)[0]
  return !!first && isSuffixToken(first)
}

export function classifyLegacyName(raw: string): ClassifyResult {
  const name = (raw ?? '').trim()
  if (!name) return { tier: 'D' }

  // Junk / note demotions — never auto-fix, always review.
  if (hasNoteMarker(name)) return { tier: 'C', reason: 'note_like' }
  if (/\d/.test(name) || name.includes('(') || name.includes(')')) return { tier: 'C', reason: 'parse_failure' }

  const hasAmp = name.includes('&')
  const coupleAnd = detectCoupleAnd(name)

  // Couples (Tier B split, or Tier C when org-like / different surnames).
  if (hasAmp || coupleAnd) {
    if (hasOrgWord(name)) return { tier: 'C', reason: 'org_like' }
    const split = splitCoupleName(name)
    if (!split) return { tier: 'C', reason: 'parse_failure' }
    if (!split.sharedSurname) return { tier: 'C', reason: 'different_surnames', suggestion: { persons: split.persons } }
    return { tier: 'B', persons: split.persons, display: split.display }
  }

  // Single-comma swap (Tier A), or Tier C when org / multi-comma / ambiguous.
  if (name.includes(',')) {
    if (commaFollowedByCorpSuffix(name)) return { tier: 'C', reason: 'org_suffix' }
    if (hasOrgWord(name)) return { tier: 'C', reason: 'org_like' }
    if ((name.match(/,/g) || []).length > 1) return { tier: 'C', reason: 'multi_comma' }
    if (commaFollowedBySuffix(name)) return { tier: 'D' } // legit "First Last, Jr."
    const beforeComma = name.slice(0, name.indexOf(',')).trim()
    if (/\s/.test(beforeComma)) return { tier: 'C', reason: 'parse_failure' } // multi-token surname → ambiguous
    const parsed = parsePersonName(name)
    if (!parsed.firstName || !parsed.lastName) return { tier: 'C', reason: 'parse_failure' }
    return { tier: 'A', proposed: parsed.display }
  }

  return { tier: 'D' }
}

// ---------------------------------------------------------------------------
// Input validation — person and organization name fields (create + edit).
// ---------------------------------------------------------------------------

export type NameValidationCode =
  | 'comma'
  | 'org_suffix'
  | 'couple_amp'
  | 'couple_and'
  | 'joiner'
  | 'email'
  | 'phone'
  | 'address'
  | 'charset'

export type NameSuggestion =
  | { kind: 'swap'; value: string }
  | { kind: 'split'; persons: string[] }

export interface NameValidationOk {
  ok: true
  /** Spec version that produced this result (NAME_NORMALIZE_MARKER). */
  spec: string
}
export interface NameValidationError {
  ok: false
  code: NameValidationCode
  message: string
  suggestion?: NameSuggestion
  spec: string
}
export type NameValidationResult = NameValidationOk | NameValidationError

function accept(): NameValidationOk {
  return { ok: true, spec: NAME_NORMALIZE_MARKER }
}
function reject(code: NameValidationCode, message: string, suggestion?: NameSuggestion): NameValidationError {
  return suggestion
    ? { ok: false, code, message, suggestion, spec: NAME_NORMALIZE_MARKER }
    : { ok: false, code, message, spec: NAME_NORMALIZE_MARKER }
}

function isEmailShaped(name: string): boolean {
  return name.includes('@') || /[\w.+-]+@[\w.-]+\.\w+/.test(name)
}
function isPhoneShaped(name: string): boolean {
  return name.replace(/\D/g, '').length >= 7
}
function isAddressShaped(name: string): boolean {
  if (/^\s*\d+\s+\p{L}/u.test(name)) return true // leading house number + word
  return /^\s*(apt\.?|apartment|unit|suite|ste\.?|bldg|building|floor|fl\.?|p\.?\s*o\.?\s*box|po\s*box|#)\b/i.test(name)
}

// PERSON-name charset guard (cp-create-main-v1): letters (any script, incl.
// accents), spaces, apostrophes (' and ’), hyphens, and periods ONLY. Commas
// are admitted HERE because the comma rules below own them (specific errors +
// one-click fixes); '&' '+' ';' '/' '@' and digit shapes are caught by their
// own specific rules BEFORE this guard runs. Organization names are exempt
// (validateOrgNameInput never applies this — orgs legitimately carry digits
// and symbols).
const PERSON_NAME_CHARSET_RE = /^[\p{L}\s'’.,\-]+$/u

function coupleSuggestion(name: string): NameSuggestion | undefined {
  const s = splitCoupleName(name)
  return s && s.persons.length >= 2 ? { kind: 'split', persons: s.persons } : undefined
}
function swapSuggestion(name: string): NameSuggestion | undefined {
  if ((name.match(/,/g) || []).length !== 1) return undefined
  if (commaFollowedBySuffix(name) || commaFollowedByCorpSuffix(name)) return undefined
  const beforeComma = name.slice(0, name.indexOf(',')).trim()
  if (!beforeComma || /\s/.test(beforeComma)) return undefined
  const parsed = parsePersonName(name)
  if (!parsed.firstName || !parsed.lastName) return undefined
  return { kind: 'swap', value: parsed.display }
}

/**
 * Validate a PERSON name input (contact_persons.full_name, or the preferred_name
 * a user types on a person-type client). Returns {ok:true} or a typed error with
 * a one-click suggestion for the two repairable shapes (comma swap, couple split).
 */
export function validatePersonNameInput(raw: string): NameValidationResult {
  const name = (raw ?? '').trim()
  if (!name) return accept() // empty is not a validation error (required-field logic is separate)

  if (isEmailShaped(name)) return reject('email', 'This looks like an email address, not a name.')
  if (isPhoneShaped(name)) return reject('phone', 'This looks like a phone number, not a name.')
  if (isAddressShaped(name)) return reject('address', 'This looks like a street address, not a name.')

  if (name.includes('&')) {
    return reject('couple_amp', 'Two people joined by "&" — split them into separate contact people.', coupleSuggestion(name))
  }
  if (detectCoupleAndLoose(name)) {
    return reject('couple_and', 'Two people joined by "and" — split them into separate contact people.', coupleSuggestion(name))
  }
  if (/[+;/]/.test(name)) {
    return reject('joiner', 'Enter one person per contact — remove the "+", ";" or "/".')
  }

  // Charset guard runs BEFORE the comma rules so a legit suffix comma
  // ("John Smith, Jr.") can't smuggle digits/symbols past validation via the
  // comma branch's early accept. Commas themselves are allowlisted here —
  // the comma rules below own them.
  if (!PERSON_NAME_CHARSET_RE.test(name)) {
    return reject('charset', "Names can't contain numbers or symbols.")
  }

  if (name.includes(',')) {
    if (commaFollowedByCorpSuffix(name)) {
      return reject('org_suffix', 'This looks like an organization — switch the type to Organization.')
    }
    if ((name.match(/,/g) || []).length === 1 && commaFollowedBySuffix(name)) {
      return accept() // "John Smith, Jr." — legit suffix comma
    }
    return reject('comma', 'Enter the name as "First Last", not "Last, First".', swapSuggestion(name))
  }

  return accept()
}

/**
 * Validate an ORGANIZATION name input. '&', ' and ', and commas (corporate
 * suffixes) are ALL allowed for orgs (308 '&' rows, 22 ' and ' rows in prod);
 * only email/phone/street-address shapes are rejected.
 */
export function validateOrgNameInput(raw: string): NameValidationResult {
  const name = (raw ?? '').trim()
  if (!name) return accept()
  if (isEmailShaped(name)) return reject('email', 'This looks like an email address, not an organization name.')
  if (isPhoneShaped(name)) return reject('phone', 'This looks like a phone number, not an organization name.')
  if (isAddressShaped(name)) return reject('address', 'This looks like a street address, not an organization name.')
  return accept()
}

// ===========================================================================
// EXTENDED REVIEW-ONLY SCAN (marker cp-name-extended-v1)
// ---------------------------------------------------------------------------
// Two NEW candidate CLASSES that train-1's comma/'&' auto-fix never surfaced,
// queued into the SAME name_fix_review table for HUMAN confirmation — this pass
// NEVER changes a name by itself:
//
//   CLASS 1 — no-comma "Last First" swap ("Friedsam Ron" → "Ron Friedsam").
//     There is no punctuation to key off, so it needs a given-name DICTIONARY.
//     HYBRID rule (live-measured ~85% precision at 94 candidates): the POSITIVE
//     "token2 is a given name" test uses a CORPUS-DERIVED dict (built at scan
//     time from clean two-token person names — captures the local South-FL name
//     distribution); the NEGATIVE "token1 is NOT a given name" veto uses that
//     corpus dict UNIONED with an embedded CENSUS list (the corpus cannot prove
//     a token is not-a-given-name, so a locally-rare real given name — Caleb,
//     Fiona, Jessie — would wrongly pass the veto without census backup). Both
//     tokens len>=2 (kills initials "Richmond A"); token1 not a compound-surname
//     prefix (kills mis-split "Mac Donald"/"Neill O"); structural org/digit/note
//     vetoes reused. Review-only ⇒ the ~5 correctly-ordered Korean FPs ("Sung
//     Kim") a reviewer rejects, or the requireKnownSurname toggle (surname_hits
//     >=1 → 54 candidates, ~92%) excludes.
//
//   CLASS 2 — couple typed as an ORGANIZATION ("Craig & Lisa Lake" → retype to
//     person + split [Craig Lake, Lisa Lake]). ONLY the single-'&' "Given &
//     Given Surname" shape (the ' and ' path found 0 couples among 22 legit
//     businesses — OFF by default). BOTH first-name slots must be given names
//     (the firm-vs-couple discriminator: rejects law-firm "Bashaw & Dech" /
//     "Rosenthal & Johnson"), no org word in ANY token (rejects HOA/POA/community
//     names "Town & Country HOA", "H & S Holding", and the "…Title"/"…Realty"
//     surname-slot org words), single shared surname.
//
// PURE — the corpus dicts are passed IN (unit-testable without D1). The Cloudflare
// worker carries a hand-mirrored copy of these two classifiers (it cannot import
// src/lib) — same precedent as classifyLegacyName / validateContactDisplayName.
// ===========================================================================

/** Marker stamped on every extended-scan result + the server audit action.
 *  Ship LIVE-verify greps the served bundle for this literal. */
export const NAME_NORMALIZE_EXT_MARKER = 'cp-name-extended-v1'

/**
 * Compound-surname prefixes: a first token equal to one of these is almost
 * always a mis-split multi-part surname ("Mac Donald" = MacDonald, "Neill O" =
 * O'Neill, "De Angelis"), NOT a swapped "Last First" — veto it. Lowercased,
 * trailing period stripped before lookup.
 */
export const COMPOUND_SURNAME_PREFIXES = new Set<string>([
  'mac', 'mc', 'o', 'st', 'saint', 'de', 'del', 'della', 'di', 'da', 'la', 'le',
  'van', 'von', 'du', 'dela', 'los', 'las', 'san', 'santa', 'ten', 'ter',
  'dr', 'mr', 'mrs', 'ms', 'mister',
])

/**
 * ADDITIONAL organization words for the extended scan's org-word veto, layered
 * ON TOP of the train-1 ORG_WORDS (which is intentionally left unmodified so the
 * train-1 classifiers keep their measured behavior). Any token match vetoes a
 * Class-2 couple candidate ("…Title", "…Realty", HOA/POA community names).
 */
export const ORG_WORDS_EXT = [
  'POA', 'Villas', 'Villa', 'Courts', 'Court', 'Chateau', 'Mall', 'Shoppes',
  'Shoppe', 'Shop', 'Warehouse', 'Cc', 'Title', 'Towing', 'Rental', 'Rentals',
  'Building', 'Holding', 'POAs', 'Condominiums',
] as const

const ORG_WORD_EXT_SET = new Set(
  [...ORG_WORDS, ...ORG_WORDS_EXT].map((t) => t.replace(/[.,]/g, '').toUpperCase()),
)

function tokenLower(tok: string): string {
  return tok.replace(/\.$/, '').replace(/[.,]/g, '').toLowerCase()
}

function hasOrgWordExt(name: string): boolean {
  const toks = name.split(/[\s,&]+/).filter(Boolean)
  for (const t of toks) {
    if (ORG_WORD_EXT_SET.has(t.replace(/[.,]/g, '').toUpperCase())) return true
  }
  return false
}

/**
 * Embedded US census given-name list (~350 lowercased tokens). Source: US SSA
 * top given names aggregated across decades (public-domain popular-name data,
 * male + female), curated to include locally-rare-but-real given names the
 * corpus dict under-ranks (Caleb, Fiona, Hayley, Jonah, Reuben, Jessie). It is
 * the NEGATIVE veto ONLY (token1 "is this maybe a given name?") — never the
 * positive test, which stays corpus-driven. Deliberately EXCLUDES surname-like
 * ambiguous givens (Mitchell, Russell) so those borderline swaps still surface
 * for human review, per the scout's 94-candidate calibration.
 */
export const CENSUS_GIVEN_NAMES = new Set<string>([
  // --- male ---
  'aaron', 'adam', 'adrian', 'aidan', 'aiden', 'alan', 'albert', 'alberto', 'alex',
  'alexander', 'alfred', 'alfredo', 'allen', 'alvin', 'andre', 'andres', 'andrew',
  'andy', 'angel', 'angelo', 'anthony', 'antonio', 'arnold', 'arthur', 'austin',
  'barry', 'ben', 'benjamin', 'bernard', 'bill', 'billy', 'blake', 'bob', 'bobby',
  'brad', 'bradley', 'brandon', 'brendan', 'brent', 'brett', 'brian', 'bruce',
  'bryan', 'bryce', 'byron', 'caleb', 'calvin', 'cameron', 'carl', 'carlos',
  'casey', 'cecil', 'cesar', 'chad', 'charles', 'charlie', 'chase', 'chester',
  'chris', 'christian', 'christopher', 'clarence', 'claude', 'clay', 'clayton',
  'clement', 'cliff', 'clifford', 'clint', 'clinton', 'clyde', 'cody', 'cole',
  'colin', 'connor', 'conrad', 'corey', 'cory', 'craig', 'curtis', 'dale', 'dan',
  'daniel', 'danny', 'darrell', 'darren', 'daryl', 'dave', 'david', 'dean',
  'dennis', 'derek', 'derrick', 'devin', 'dexter', 'dominic', 'dominick', 'don',
  'donald', 'douglas', 'drew', 'duane', 'dustin', 'dwayne', 'dwight', 'earl',
  'eddie', 'edgar', 'edmund', 'eduardo', 'edward', 'edwin', 'elijah', 'elliot',
  'elmer', 'emanuel', 'emmanuel', 'enrique', 'eric', 'erik', 'ernest', 'ernesto',
  'ethan', 'eugene', 'evan', 'everett', 'ezra', 'felix', 'fernando', 'floyd',
  'forrest', 'francis', 'francisco', 'frank', 'franklin', 'fred', 'freddie',
  'frederick', 'gabriel', 'gary', 'gene', 'george', 'gerald', 'gilbert', 'glen',
  'glenn', 'gordon', 'grant', 'greg', 'gregory', 'harold', 'harry', 'harvey',
  'hector', 'henry', 'herbert', 'herman', 'howard', 'hugh', 'hunter', 'ian',
  'isaac', 'ivan', 'jack', 'jackson', 'jacob', 'jaime', 'jake', 'james', 'jared',
  'jason', 'javier', 'jay', 'jeff', 'jeffery', 'jeffrey', 'jerome', 'jerry',
  'jesse', 'jessie', 'jesus', 'jim', 'jimmy', 'joe', 'joel', 'joey', 'john',
  'johnny', 'jon', 'jonah', 'jonathan', 'jordan', 'jorge', 'jose', 'joseph',
  'joshua', 'juan', 'julian', 'julio', 'justin', 'karl', 'keith', 'kelly',
  'ken', 'kenneth', 'kent', 'kevin', 'kirk', 'kurt', 'kyle', 'lance', 'larry',
  'lawrence', 'lee', 'leo', 'leon', 'leonard', 'leroy', 'leslie', 'lester',
  'levi', 'lewis', 'lloyd', 'logan', 'lonnie', 'louis', 'lucas', 'luis', 'luke',
  'manuel', 'marc', 'marcus', 'mario', 'mark', 'marshall', 'martin', 'marvin',
  'mason', 'mathew', 'matthew', 'maurice', 'max', 'melvin', 'micah', 'michael',
  'miguel', 'mike', 'milton', 'morris', 'nathan', 'nathaniel', 'neal', 'neil',
  'nelson', 'nicholas', 'nick', 'noah', 'noel', 'norman', 'oliver', 'oscar',
  'owen', 'pablo', 'patrick', 'paul', 'pedro', 'perry', 'pete', 'peter', 'philip',
  'phillip', 'preston', 'quentin', 'ralph', 'ramon', 'randall', 'randy', 'raul',
  'ray', 'raymond', 'reginald', 'rene', 'reuben', 'rex', 'ricardo', 'richard',
  'rick', 'ricky', 'robert', 'roberto', 'rodney', 'roger', 'roland', 'roman',
  'ron', 'ronald', 'ronnie', 'roy', 'ruben', 'rudy', 'rufus', 'ryan', 'sam',
  'samuel', 'saul', 'scott', 'sean', 'sergio', 'seth', 'shane', 'shaun', 'shawn',
  'sheldon', 'sidney', 'simon', 'spencer', 'stanley', 'stephen', 'steve',
  'steven', 'stewart', 'stuart', 'ted', 'terrance', 'terrence', 'terry',
  'theodore', 'thomas', 'tim', 'timothy', 'toby', 'todd', 'tom', 'tommy', 'tony',
  'travis', 'trevor', 'troy', 'tyler', 'tyrone', 'vernon', 'victor', 'vincent',
  'virgil', 'wade', 'wallace', 'walter', 'warren', 'wayne', 'wesley', 'willard',
  'william', 'willie', 'wilson', 'zachary',
  // --- female ---
  'abigail', 'ada', 'adriana', 'agnes', 'aimee', 'alexandra', 'alexis', 'alice',
  'alicia', 'alison', 'allison', 'alma', 'alyssa', 'amanda', 'amber', 'amelia',
  'amy', 'ana', 'andrea', 'angela', 'angelina', 'anita', 'ann', 'anna', 'anne',
  'annette', 'annie', 'april', 'ariana', 'arlene', 'ashley', 'audrey', 'barbara',
  'beatrice', 'becky', 'belinda', 'bernice', 'bertha', 'beth', 'bethany', 'betty',
  'beverly', 'bianca', 'bonnie', 'brandy', 'brenda', 'briana', 'bridget',
  'brittany', 'brooke', 'candace', 'candice', 'carla', 'carmen', 'carol',
  'carole', 'caroline', 'carolyn', 'carrie', 'casey', 'cassandra', 'catherine',
  'cathy', 'cecilia', 'charlene', 'charlotte', 'chelsea', 'cheryl', 'christina',
  'christine', 'christy', 'cindy', 'claire', 'clara', 'claudia', 'colleen',
  'connie', 'constance', 'courtney', 'crystal', 'cynthia', 'daisy', 'dana',
  'danielle', 'daphne', 'darlene', 'dawn', 'deanna', 'debbie', 'deborah', 'debra',
  'delia', 'denise', 'diana', 'diane', 'dianne', 'dolores', 'donna', 'dora',
  'doreen', 'doris', 'dorothy', 'edith', 'edna', 'eileen', 'elaine', 'eleanor',
  'elena', 'elise', 'elizabeth', 'ella', 'ellen', 'eloise', 'elsie', 'emily',
  'emma', 'erica', 'erika', 'erin', 'esther', 'ethel', 'eva', 'evelyn', 'faith',
  'fannie', 'fiona', 'flora', 'florence', 'frances', 'francesca', 'gabriela',
  'gail', 'gayle', 'gena', 'genevieve', 'georgia', 'geraldine', 'gina', 'ginger',
  'gladys', 'gloria', 'grace', 'gwen', 'gwendolyn', 'hannah', 'harriet', 'hayley',
  'hazel', 'heather', 'heidi', 'helen', 'henrietta', 'holly', 'hope', 'ida',
  'irene', 'iris', 'isabel', 'isabella', 'ivy', 'jackie', 'jacqueline', 'jamie',
  'jane', 'janet', 'janice', 'jasmine', 'jean', 'jeanette', 'jeanne', 'jenna',
  'jennifer', 'jenny', 'jessica', 'jill', 'joan', 'joann', 'joanna', 'joanne',
  'jocelyn', 'jodi', 'jody', 'jolene', 'josephine', 'joy', 'joyce', 'judith',
  'judy', 'julia', 'julie', 'june', 'karen', 'karla', 'kate', 'katherine',
  'kathleen', 'kathryn', 'kathy', 'katie', 'katrina', 'kay', 'kayla', 'kelly',
  'kelsey', 'kendra', 'kerry', 'kim', 'kimberly', 'kirsten', 'krista', 'kristen',
  'kristin', 'kristina', 'kristine', 'lana', 'laura', 'lauren', 'laurie',
  'leah', 'lena', 'leslie', 'lillian', 'lily', 'linda', 'lindsay', 'lindsey',
  'lisa', 'lois', 'lora', 'loretta', 'lori', 'lorraine', 'louise', 'lucia',
  'lucille', 'lucy', 'lydia', 'lynda', 'lynn', 'lynne', 'mabel', 'madeline',
  'madison', 'mae', 'maggie', 'mandy', 'marcia', 'margaret', 'margie', 'maria',
  'marian', 'marie', 'marilyn', 'marion', 'marjorie', 'marlene', 'marsha',
  'martha', 'mary', 'maureen', 'megan', 'melanie', 'melinda', 'melissa', 'mercedes',
  'meredith', 'mia', 'michelle', 'mildred', 'mindy', 'miriam', 'misty', 'molly',
  'monica', 'nadine', 'nancy', 'naomi', 'natalie', 'natasha', 'nell', 'nellie',
  'nicole', 'nina', 'nora', 'norma', 'olga', 'olivia', 'opal', 'paige', 'pam',
  'pamela', 'patricia', 'patsy', 'patty', 'paula', 'pauline', 'pearl', 'peggy',
  'penny', 'phyllis', 'priscilla', 'rachel', 'ramona', 'rebecca', 'regina',
  'renee', 'rhonda', 'rita', 'roberta', 'robin', 'rochelle', 'rosa', 'rose',
  'rosemary', 'roxanne', 'ruby', 'ruth', 'sabrina', 'sally', 'samantha', 'sandra',
  'sara', 'sarah', 'shannon', 'sharon', 'sheila', 'shelby', 'shelly', 'sherri',
  'sherry', 'shirley', 'sofia', 'sonia', 'sophia', 'stacey', 'stacy', 'stella',
  'stephanie', 'sue', 'susan', 'suzanne', 'sylvia', 'tabitha', 'tamara', 'tammy',
  'tanya', 'tara', 'teresa', 'teri', 'terri', 'thelma', 'theresa', 'tiffany',
  'tina', 'tonya', 'tracey', 'tracy', 'valerie', 'vanessa', 'vera', 'veronica',
  'vicki', 'vickie', 'victoria', 'viola', 'violet', 'virginia', 'vivian', 'wanda',
  'wendy', 'whitney', 'wilma', 'yolanda', 'yvette', 'yvonne', 'zoe',
])

// ---------------------------------------------------------------------------
// CLASS 1 — classifyNoCommaSwap
// ---------------------------------------------------------------------------

export interface NoCommaSwapConfig {
  /** Corpus-derived confident given-name dict (lowercased). The POSITIVE signal:
   *  token2 ∈ givenDict ⇒ "token2 is a given name". Built at scan time. */
  givenDict: Set<string>
  /** Corpus surname frequency for a lowercased token (0 when unknown). Drives
   *  surnameHits + the requireKnownSurname high-precision toggle. */
  surnameHits?: (tokenLower: string) => number
  /** High-precision mode (94→54 rows, ~92%): additionally require token1 to be a
   *  known corpus surname (surnameHits>=1). Excludes the Korean "Sung Kim" FPs. */
  requireKnownSurname?: boolean
  /** token1 negative-veto set, UNIONED with givenDict. Defaults to
   *  CENSUS_GIVEN_NAMES (the corpus cannot disprove a rare real given name). */
  census?: Set<string>
}

export interface NoCommaSwapResult {
  flagged: boolean
  /** Present when flagged: the proposed corrected "First Last" (token2 token1). */
  proposed?: string
  token1?: string
  token2?: string
  surnameHits?: number
  surnameConfirmed?: boolean
}

/**
 * Classify a person-scope name as a no-comma "Last First" swap candidate.
 * REVIEW-ONLY: a flagged result is a SUGGESTION, never an auto-applied change.
 * Pure — pass the corpus dicts in (unit-testable without D1).
 */
export function classifyNoCommaSwap(rawName: string, config: NoCommaSwapConfig): NoCommaSwapResult {
  const name = (rawName ?? '').replace(/\s+/g, ' ').trim()
  const notFlagged: NoCommaSwapResult = { flagged: false }
  if (!name) return notFlagged

  // Structural vetoes (reuse the train-1 dirty-data guards). Any comma / '&' /
  // capitalized ' and ' / digit / paren / note marker / org word ⇒ not this class.
  if (name.includes(',') || name.includes('&') || detectCoupleAnd(name)) return notFlagged
  if (/\d/.test(name) || name.includes('(') || name.includes(')')) return notFlagged
  if (/\sfor\s/i.test(name) || /\s-\s/.test(name)) return notFlagged
  if (hasOrgWordExt(name)) return notFlagged

  const tokens = name.split(' ').filter(Boolean)
  if (tokens.length !== 2) return notFlagged // exactly "Last First"
  const [t1, t2] = tokens
  if (t1.length < 2 || t2.length < 2) return notFlagged // kills "Richmond A" / "Neill O"

  const t1l = tokenLower(t1)
  const t2l = tokenLower(t2)
  if (COMPOUND_SURNAME_PREFIXES.has(t1l)) return notFlagged // "Mac Donald" / "De Angelis"

  const census = config.census ?? CENSUS_GIVEN_NAMES
  const token2IsGiven = config.givenDict.has(t2l) // POSITIVE: corpus dict only
  const token1IsGiven = config.givenDict.has(t1l) || census.has(t1l) // NEGATIVE veto: corpus ∪ census
  if (!token2IsGiven || token1IsGiven) return notFlagged

  const surnameHits = config.surnameHits ? config.surnameHits(t1l) : 0
  const surnameConfirmed = surnameHits >= 1
  if (config.requireKnownSurname && !surnameConfirmed) return notFlagged

  return {
    flagged: true,
    proposed: `${t2} ${t1}`,
    token1: t1,
    token2: t2,
    surnameHits,
    surnameConfirmed,
  }
}

// ---------------------------------------------------------------------------
// CLASS 2 — classifyOrgCouple
// ---------------------------------------------------------------------------

export interface OrgCoupleConfig {
  /** The HYBRID given set (corpus givenDict UNION census) — both first-name slots
   *  of the couple must be in it (the firm-vs-couple discriminator). */
  given: Set<string>
  /** Enable the ' and '-joined path (OFF by default — measured 0 couples among
   *  22 legit ' and ' businesses). */
  allowAnd?: boolean
}

export interface OrgCoupleResult {
  flagged: boolean
  /** Present when flagged: the two split "First Last" persons. */
  persons?: string[]
  /** Present when flagged: recomposed household display ("Craig & Lisa Lake"). */
  display?: string
}

/**
 * Classify an organization-scope name as a couple mis-typed as an org.
 * REVIEW-ONLY. '&'-shape only by default. Pure — pass the given set in.
 */
export function classifyOrgCouple(rawName: string, config: OrgCoupleConfig): OrgCoupleResult {
  const name = (rawName ?? '').replace(/\s+/g, ' ').trim()
  const notFlagged: OrgCoupleResult = { flagged: false }
  if (!name) return notFlagged

  if (/\d/.test(name) || name.includes('(') || name.includes(')')) return notFlagged
  const ampCount = (name.match(/&/g) || []).length
  const hasAnd = config.allowAnd === true && detectCoupleAnd(name)
  // '&'-shape by default; the ' and ' path only when explicitly enabled AND no '&'.
  if (ampCount === 1) {
    /* proceed */
  } else if (ampCount === 0 && hasAnd) {
    /* proceed */
  } else {
    return notFlagged
  }

  // Org word in ANY token vetoes (HOA/POA/community names + "…Title"/"…Realty").
  if (hasOrgWordExt(name)) return notFlagged

  const split = splitCoupleName(name)
  if (!split || !split.sharedSurname || split.persons.length < 2) return notFlagged

  // BOTH first-name slots must be given names (rejects "Bashaw & Dech").
  const f1 = tokenLower(parsePersonName(split.persons[0]).firstName)
  const f2 = tokenLower(parsePersonName(split.persons[1]).firstName)
  if (!f1 || !f2 || !config.given.has(f1) || !config.given.has(f2)) return notFlagged

  return { flagged: true, persons: split.persons, display: split.display }
}
